import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  FileText, Upload, Download, Search, Filter, Clock,
  Shield, CheckCircle, XCircle, AlertTriangle, Eye,
  Trash2, FolderOpen, FileCheck, FilePlus, RefreshCw,
  Lock, Unlock, ChevronLeft, Calendar, User, Tag,
  CheckSquare, FileWarning, Activity, Brain
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_CATEGORIES = [
  { value: "clinical", label: "Clinical Notes", icon: FileText },
  { value: "admission", label: "Admission Documents", icon: FilePlus },
  { value: "consent", label: "Consent Forms", icon: FileCheck },
  { value: "medication", label: "Medication Records", icon: Shield },
  { value: "lab_results", label: "Lab Results", icon: Activity },
  { value: "orders", label: "Physician Orders", icon: FileText },
  { value: "care_plan", label: "Care Plans", icon: Brain },
  { value: "assessment", label: "Assessments", icon: CheckSquare },
  { value: "other", label: "Other", icon: FolderOpen }
];

const DOCUMENT_STATUS = {
  pending_qa: { label: "Pending QA", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  qa_passed: { label: "QA Passed", color: "bg-green-100 text-green-800", icon: CheckCircle },
  qa_failed: { label: "QA Failed", color: "bg-red-100 text-red-800", icon: XCircle },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-800", icon: CheckCircle },
  archived: { label: "Archived", color: "bg-gray-100 text-gray-800", icon: FolderOpen }
};

export default function DocumentManagement() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [qaResults, setQaResults] = useState(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending_qa: 0,
    qa_passed: 0,
    qa_failed: 0,
    approved: 0
  });

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    chart_id: "",
    category: "clinical",
    description: "",
    file: null
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("hospice_token");
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("hospice_token");
      const params = new URLSearchParams();
      if (filterCategory !== "all") params.append("category", filterCategory);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (searchTerm) params.append("search", searchTerm);
      params.append("limit", "100");

      const response = await fetch(`${API_URL}/api/ehr/documents/?${params}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setStats(data.stats || stats);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus, searchTerm]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.chart_id) {
      toast.error("Please select a file and enter patient chart ID");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("chart_id", uploadForm.chart_id);
      formData.append("category", uploadForm.category);
      if (uploadForm.description) formData.append("description", uploadForm.description);

      const token = localStorage.getItem("hospice_token");
      
      // Simulate progress for UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${API_URL}/api/ehr/documents/upload`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        toast.success("Document uploaded successfully!");
        setUploadDialog(false);
        setUploadForm({ chart_id: "", category: "clinical", description: "", file: null });
        fetchDocuments();
        
        // Auto-trigger QA if it's a clinical document
        if (uploadForm.category === "clinical" || uploadForm.category === "assessment") {
          runQACheck(result.document_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const runQACheck = async (documentId) => {
    try {
      setQaLoading(true);
      const token = localStorage.getItem("hospice_token");
      
      const response = await fetch(`${API_URL}/api/ehr/documents/qa/run/${documentId}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setQaResults(result);
        toast.success(result.passed ? "QA Check Passed!" : "QA Check Found Issues");
        fetchDocuments();
      } else {
        toast.error("QA check failed");
      }
    } catch (error) {
      console.error("QA error:", error);
      toast.error("Failed to run QA check");
    } finally {
      setQaLoading(false);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API_URL}/api/ehr/documents/download/${documentId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Document downloaded");
      } else {
        toast.error("Download failed");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download document");
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API_URL}/api/ehr/documents/${documentId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Document deleted");
        fetchDocuments();
        setSelectedDocument(null);
      } else {
        toast.error("Failed to delete document");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document");
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        doc.filename?.toLowerCase().includes(search) ||
        doc.chart_id?.toLowerCase().includes(search) ||
        doc.description?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getCategoryIcon = (category) => {
    const cat = DOCUMENT_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : FileText;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-teal-500/30"
                onClick={() => navigate("/hospice-ehr")}
                data-testid="back-to-ehr-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" /> Back to EHR
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  <FolderOpen className="w-7 h-7" />
                  Document Management
                </h1>
                <p className="text-teal-100 mt-1">HIPAA-Compliant Document Storage & QA</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                className="text-white hover:bg-teal-500/30"
                onClick={fetchDocuments}
                data-testid="refresh-docs-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-teal-700 hover:bg-teal-50" data-testid="upload-doc-btn">
                    <Upload className="w-4 h-4 mr-2" /> Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Upload New Document</DialogTitle>
                    <DialogDescription>
                      Upload a HIPAA-compliant document to the patient chart
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Patient Chart ID *</Label>
                      <Input
                        placeholder="Enter chart ID..."
                        value={uploadForm.chart_id}
                        onChange={(e) => setUploadForm({...uploadForm, chart_id: e.target.value})}
                        data-testid="upload-chart-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Document Category *</Label>
                      <Select
                        value={uploadForm.category}
                        onValueChange={(value) => setUploadForm({...uploadForm, category: value})}
                      >
                        <SelectTrigger data-testid="upload-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Textarea
                        placeholder="Brief description of the document..."
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                        rows={2}
                        data-testid="upload-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Select File *</Label>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                        onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                        data-testid="upload-file-input"
                      />
                      <p className="text-xs text-gray-500">
                        Supported: PDF, DOC, DOCX, TXT, PNG, JPG (Max 50MB)
                      </p>
                    </div>
                    {uploading && (
                      <div className="space-y-2">
                        <Progress value={uploadProgress} className="h-2" />
                        <p className="text-sm text-center text-gray-600">
                          {uploadProgress < 100 ? "Uploading & Encrypting..." : "Processing..."}
                        </p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
                    <Button
                      onClick={handleUpload}
                      disabled={uploading || !uploadForm.file || !uploadForm.chart_id}
                      data-testid="confirm-upload-btn"
                    >
                      {uploading ? "Uploading..." : "Upload Document"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <FileText className="w-8 h-8 text-slate-600" />
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Documents</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-800">{stats.pending_qa}</p>
                <p className="text-xs text-yellow-600">Pending QA</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-800">{stats.qa_passed}</p>
                <p className="text-xs text-green-600">QA Passed</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-800">{stats.qa_failed}</p>
                <p className="text-xs text-red-600">QA Failed</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <Shield className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-800">{stats.approved}</p>
                <p className="text-xs text-blue-600">Approved</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Document List */}
          <div className="col-span-8">
            <Card className="shadow-sm">
              <CardHeader className="border-b bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Documents</CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search documents..."
                        className="pl-9 w-64"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="search-docs-input"
                      />
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-40" data-testid="filter-category-select">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {DOCUMENT_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-36" data-testid="filter-status-select">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {Object.entries(DOCUMENT_STATUS).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
                    <span className="ml-2 text-gray-600">Loading documents...</span>
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No documents found</p>
                    <Button
                      variant="link"
                      onClick={() => setUploadDialog(true)}
                      className="text-teal-600"
                    >
                      Upload your first document
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredDocuments.map(doc => {
                      const CategoryIcon = getCategoryIcon(doc.category);
                      const status = DOCUMENT_STATUS[doc.qa_status] || DOCUMENT_STATUS.pending_qa;
                      const StatusIcon = status.icon;
                      
                      return (
                        <div
                          key={doc.id}
                          className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                            selectedDocument?.id === doc.id ? "bg-teal-50 border-l-4 border-teal-500" : ""
                          }`}
                          onClick={() => setSelectedDocument(doc)}
                          data-testid={`doc-row-${doc.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="p-2 bg-slate-100 rounded-lg">
                              <CategoryIcon className="w-6 h-6 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900 truncate">{doc.filename}</h4>
                                <Badge className={status.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {status.label}
                                </Badge>
                                {doc.encrypted && (
                                  <Lock className="w-4 h-4 text-green-600" title="HIPAA Encrypted" />
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" /> Chart: {doc.chart_id}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Tag className="w-3 h-3" /> {DOCUMENT_CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" /> {new Date(doc.uploaded_at).toLocaleDateString()}
                                </span>
                                <span>{formatFileSize(doc.file_size || 0)}</span>
                              </div>
                              {doc.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-1">{doc.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); downloadDocument(doc.id, doc.filename); }}
                                data-testid={`download-doc-${doc.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); runQACheck(doc.id); }}
                                disabled={qaLoading}
                                data-testid={`qa-doc-${doc.id}`}
                              >
                                <Brain className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Document Details & QA Panel */}
          <div className="col-span-4 space-y-4">
            {/* Document Details */}
            <Card className="shadow-sm">
              <CardHeader className="bg-slate-50/50 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" /> Document Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {selectedDocument ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500">Filename</p>
                      <p className="font-medium">{selectedDocument.filename}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Chart ID</p>
                        <p className="font-medium">{selectedDocument.chart_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Category</p>
                        <p className="font-medium">{DOCUMENT_CATEGORIES.find(c => c.value === selectedDocument.category)?.label}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Uploaded</p>
                        <p className="font-medium">{new Date(selectedDocument.uploaded_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Size</p>
                        <p className="font-medium">{formatFileSize(selectedDocument.file_size || 0)}</p>
                      </div>
                    </div>
                    {selectedDocument.description && (
                      <div>
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="text-gray-700">{selectedDocument.description}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                      {selectedDocument.encrypted && (
                        <Badge className="bg-green-100 text-green-800">
                          <Lock className="w-3 h-3 mr-1" /> HIPAA Encrypted
                        </Badge>
                      )}
                      {selectedDocument.hash_verified && (
                        <Badge className="bg-blue-100 text-blue-800">
                          <Shield className="w-3 h-3 mr-1" /> Hash Verified
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        className="flex-1"
                        onClick={() => downloadDocument(selectedDocument.id, selectedDocument.filename)}
                        data-testid="download-selected-doc"
                      >
                        <Download className="w-4 h-4 mr-2" /> Download
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => runQACheck(selectedDocument.id)}
                        disabled={qaLoading}
                        data-testid="qa-selected-doc"
                      >
                        <Brain className="w-4 h-4 mr-2" /> Run QA
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => deleteDocument(selectedDocument.id)}
                        data-testid="delete-selected-doc"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Select a document to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* QA Results Panel */}
            <Card className="shadow-sm">
              <CardHeader className="bg-slate-50/50 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Brain className="w-5 h-5" /> Real-time QA Results
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {qaLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                    <p className="mt-2 text-gray-600">Running LCD Compliance Check...</p>
                  </div>
                ) : qaResults ? (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${qaResults.passed ? "bg-green-50" : "bg-red-50"}`}>
                      <div className="flex items-center gap-2">
                        {qaResults.passed ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                        <span className={`font-semibold ${qaResults.passed ? "text-green-800" : "text-red-800"}`}>
                          {qaResults.passed ? "QA Check Passed" : "QA Check Failed"}
                        </span>
                      </div>
                      <p className="text-sm mt-1 text-gray-600">
                        Score: {qaResults.score?.toFixed(0) || 0}%
                      </p>
                    </div>

                    {qaResults.issues && qaResults.issues.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Issues Found:</p>
                        <div className="space-y-2">
                          {qaResults.issues.map((issue, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-red-50 rounded">
                              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-red-700">{issue}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {qaResults.recommendations && qaResults.recommendations.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Recommendations:</p>
                        <div className="space-y-2">
                          {qaResults.recommendations.map((rec, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                              <CheckSquare className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-blue-700">{rec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {qaResults.lcd_rules_checked && (
                      <div className="text-xs text-gray-500 mt-2">
                        Checked against {qaResults.lcd_rules_checked} LCD rules
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Run QA on a document to see compliance results</p>
                    <p className="text-xs mt-1">LCD-based real-time auditing</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
